import React, { useEffect } from 'react';
import { FeatureToggleEnum, useFeatureTogglesContext } from '../../../context/FeatureTogglesContext';
import BaseTooltip from '../BaseTooltip/BaseTooltip';
import { get, set } from 'js-cookie';
import { useMobileMedia } from '@xo-union/tk-ui-breakpoints';
import styles from './AiButtonTooltip.scss';

export const AI_BUTTON_TOOLTIP_COOKIE_NAME = 'wws_quinn_button_tooltip';

interface AiButtonTooltipCookie {
  hasSeenTooltip: boolean;
}

export const getAiButtonTooltipCookie = (): AiButtonTooltipCookie => {
  let stringifiedCookieValue = get(AI_BUTTON_TOOLTIP_COOKIE_NAME);
  if (!stringifiedCookieValue) {
    const newValue = {
      hasSeenTooltip: false,
    };
    updateAiButtonTooltipCookie(newValue);
    stringifiedCookieValue = JSON.stringify(newValue);
  }

  return JSON.parse(stringifiedCookieValue);
};

const updateAiButtonTooltipCookie = (cookieValue: AiButtonTooltipCookie) => {
  set(AI_BUTTON_TOOLTIP_COOKIE_NAME, JSON.stringify(cookieValue));
};

const onTooltipRender = (cookie: AiButtonTooltipCookie) => {
  cookie.hasSeenTooltip = true;
  updateAiButtonTooltipCookie(cookie);
};

interface AiButtonTooltipProps {
  children?: React.ReactNode;
  shouldDisableTooltip?: boolean;
  arrowOffsetFromCenterPercentage?: number;
  verticalOffset?: number;
  isButtonActive?: boolean;
}

const AiButtonTooltip = ({
  shouldDisableTooltip = false,
  arrowOffsetFromCenterPercentage = 94.5,
  verticalOffset = 8,
  children,
  isButtonActive = false,
}: AiButtonTooltipProps) => {
  const [shouldShowTooltip, setShouldShowTooltip] = React.useState(false);

  const mobileMedia = useMobileMedia();
  const shouldRenderTooltipOnTop = mobileMedia?.yes;

  const { isEnabled } = useFeatureTogglesContext();
  const isAiButtonToggleEnabled = isEnabled(FeatureToggleEnum.AiTextButton);

  const handleCloseTooltip = () => {
    setShouldShowTooltip(false);
  };

  useEffect(() => {
    // Close tooltip when the parent field loses focus
    if (!isButtonActive || shouldDisableTooltip) {
      handleCloseTooltip();
      return;
    }

    const cookie = getAiButtonTooltipCookie();
    if (cookie.hasSeenTooltip) {
      return;
    }

    setShouldShowTooltip(true);

    onTooltipRender(cookie);
  }, [isButtonActive, shouldDisableTooltip]);

  return (
    <BaseTooltip
      shouldShowTooltip={shouldShowTooltip && isAiButtonToggleEnabled}
      arrowOffsetFromCenterPercentage={arrowOffsetFromCenterPercentage}
      verticalOffset={shouldRenderTooltipOnTop ? verticalOffset + 48 : verticalOffset}
      handleCloseTooltip={handleCloseTooltip}
      title="NEW! Edit with Quinn"
      description="Add a rough draft and use AI to quickly help craft your text."
      datatestid="AiButtonTooltip"
      tooltipClassName={styles.AiButtonTooltip}
      arrowPosition={shouldRenderTooltipOnTop ? 'bottom' : 'top'}
    >
      {children}
    </BaseTooltip>
  );
};

export default AiButtonTooltip;
